import React, { useMemo, useState } from 'react'
import { X, CheckCircle, HelpCircle } from 'lucide-react'
import { projectsApi, VendorFormMatchResult } from '@/lib/projectsApi'
import { useToast } from '@/contexts/ToastContext'

interface Props {
  projectId: string
  file: File
  result: VendorFormMatchResult
  onClose: () => void
  onSuccess: () => void
}

// preview-gated routing of a vendor-response form to a project system.
// never silent-attaches: user always confirms the target. default
// selection is the matcher's best guess. high-confidence matches are
// pre-selected, lower-confidence ones still need an explicit confirm.
export const VendorRouteModal: React.FC<Props> = ({ projectId: _projectId, file, result, onClose, onSuccess }) => {
  const { success, error: showError } = useToast()
  const candidates = result.candidates ?? []
  const top = candidates[0]
  const tier: 'auto' | 'review' | 'none' = top?.confidence ?? 'none'

  // default selection: auto/review → best guess, none → force a manual pick
  const [selectedId, setSelectedId] = useState<string>(
    top && tier !== 'none' ? top.project_system_id : '',
  )
  const [committing, setCommitting] = useState(false)

  const allSystems = result.project_systems ?? []
  const id = result.identity
  const noSystemName = !(id?.system_name ?? '').trim()
  const sysName = id?.system_name ?? '(no System Name filled in)'

  const banner = useMemo(() => {
    // blank-identity form: vendor filled the Q&A but left the System
    // Name header empty. nothing to match on, go straight to manual pick.
    if (noSystemName) return { icon: <HelpCircle className="h-4 w-4 text-amber-500" />, text: 'This form has no System Name filled in — pick the system it belongs to.', cls: 'text-amber-700 dark:text-amber-300' }
    if (tier === 'auto')   return { icon: <CheckCircle className="h-4 w-4 text-emerald-500" />, text: `Matches ${top!.label} — high confidence. Confirm to attach.`, cls: 'text-emerald-700 dark:text-emerald-300' }
    if (tier === 'review') return { icon: <HelpCircle className="h-4 w-4 text-amber-500" />,   text: `Best guess: ${top!.label} (please confirm — not a confident match).`, cls: 'text-amber-700 dark:text-amber-300' }
    return { icon: <HelpCircle className="h-4 w-4 text-slate-400" />, text: 'No confident match — pick the system this form belongs to.', cls: 'text-slate-600 dark:text-slate-300' }
  }, [tier, top, noSystemName])

  const handleAttach = async () => {
    if (!selectedId || !result.parsed || !result.proposed_profile) return
    setCommitting(true)
    try {
      const r = await projectsApi.commitAssessment(selectedId, {
        parsed:           result.parsed,
        proposed_profile: result.proposed_profile,
        source_filename:  result.source_filename ?? file.name,
      })
      success(`Vendor form attached${r.is_cbs ? ' (CBS)' : ''}`)
      onSuccess()
    } catch (e: any) {
      showError(e?.message ?? 'Failed to attach vendor form')
    } finally {
      setCommitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-lg w-full overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-base font-semibold">Attach vendor form</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"><X className="h-4 w-4" /></button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* extracted identity */}
          <div className="text-sm">
            <div className="font-medium text-slate-900 dark:text-white">{sysName}</div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              {[id?.vendor_name, id?.model_name].filter(Boolean).join(' · ') || '—'} · {file.name}
            </div>
          </div>

          {/* confidence banner */}
          <div className={`flex items-center gap-2 text-xs px-3 py-2 rounded bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 ${banner.cls}`}>
            {banner.icon}<span>{banner.text}</span>
          </div>

          {/* system picker, always required, defaulted to the best guess */}
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">Attach to system</label>
            <select
              value={selectedId}
              onChange={e => setSelectedId(e.target.value)}
              className="w-full text-sm px-2 py-1.5 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
            >
              <option value="">— select a system —</option>
              {/* ranked candidates first (with scores), then the rest */}
              {candidates.length > 0 && (
                <optgroup label="Best matches">
                  {candidates.map(c => (
                    <option key={c.project_system_id} value={c.project_system_id}>
                      {c.label}{c.vendor ? ` (${c.vendor})` : ''} — {(c.score * 100).toFixed(0)}%
                    </option>
                  ))}
                </optgroup>
              )}
              <optgroup label="All systems">
                {allSystems.map(s => (
                  <option key={s.id} value={s.id}>{s.label}{s.vendor ? ` (${s.vendor})` : ''}</option>
                ))}
              </optgroup>
            </select>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 dark:border-slate-700">
          <button onClick={onClose} disabled={committing} className="px-3 py-1.5 text-xs border border-slate-200 dark:border-slate-700 rounded hover:bg-slate-50 dark:hover:bg-slate-700">Cancel</button>
          <button onClick={handleAttach} disabled={committing || !selectedId}
                  className="px-3 py-1.5 text-xs bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded hover:opacity-90 disabled:opacity-50">
            {committing ? 'Attaching…' : 'Attach here'}
          </button>
        </div>
      </div>
    </div>
  )
}
